import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts';
import { Users, X } from 'lucide-react';
import { getPlayers, getTechnicalStats, getTechnicalKpiBreakdown } from '../api';
import PageChrome from '../components/PageChrome';
import MultiSelect from '../components/MultiSelect';
import { PLAYER_COLORS } from './gps/InMatchAnalysis';
import { ScoreCell, KpiCell } from './TechnicalOverview';

// Same GK exclusion as TechnicalOverview.js — no Technical KPIs are seeded for GK.
const POSITIONS = ['CB', 'FB', 'CDM', 'CAM', 'ST', 'W'];
const ACCENT = '#f59e0b';
const MAX_COMPARE = 4;
// Goals/Assists are shown as name badges on the team overview table, not columns — see
// TechnicalOverview.js's own NAME_BADGE_KPIS. Excluded here for the same reason (they're match
// events, not per-position KPIs) rather than reimplemented as badges on this page.
const NAME_BADGE_KPIS = new Set(['Goals', 'Assists']);
// ACCENT (#f59e0b) is also PLAYER_COLORS[2] — drop it from the compare palette so a compare
// player never collides with the primary player's fixed accent color.
const COMPARE_COLORS = PLAYER_COLORS.filter(c => c !== ACCENT);

// Adapted from IndividualReport.js's ComparePlayerPicker — same dropdown-checklist pattern,
// capped selection, primary player excluded from its own candidate list.
function ComparePlayerPicker({ players, selected, onChange, max, excludeId }) {
  const [open, setOpen] = useState(false);
  const candidates = players.filter(p => String(p.id) !== String(excludeId));
  const toggle = id => {
    const idStr = String(id);
    if (selected.includes(idStr)) onChange(selected.filter(s => s !== idStr));
    else if (selected.length < max) onChange([...selected, idStr]);
  };
  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13 }}>
        <Users size={14} /> {selected.length ? `Comparing ${selected.length}` : 'Add players to compare'}
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 50, background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 10, minWidth: 220, maxHeight: 280, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}
          onMouseLeave={() => setOpen(false)}>
          <div style={{ padding: '6px 12px', fontSize: 11, color: 'var(--text-dimmed)' }}>Up to {max} players</div>
          {candidates.map(p => {
            const idStr = String(p.id);
            const checked = selected.includes(idStr);
            const disabled = !checked && selected.length >= max;
            return (
              <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', cursor: disabled ? 'default' : 'pointer', fontSize: 13, color: disabled ? 'var(--text-dimmed)' : 'var(--text-primary)' }}>
                <input type="checkbox" checked={checked} disabled={disabled} onChange={() => toggle(p.id)} />
                {p.name}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ComparisonTooltip({ active, payload, label, playerNames }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 8, padding: '8px 12px', fontSize: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
      <div style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-secondary)' }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: p.stroke, display: 'inline-block' }} />
          {playerNames[p.dataKey] || p.dataKey}: <strong style={{ color: 'var(--text-primary)' }}>{p.value == null ? 'no data' : Math.round(p.value)}</strong>
        </div>
      ))}
    </div>
  );
}

// One solid line per selected player (not per metric — there's only one metric, Technical score,
// so color alone disambiguates players; no dash patterns needed).
function TechnicalScoreTrendChart({ players, matchesOrdered, allRowsByMatchId, colorFor }) {
  const chartData = useMemo(() => matchesOrdered.map(m => {
    const row = { label: m.label };
    const rows = allRowsByMatchId[m.matchId] || [];
    players.forEach(p => {
      const found = rows.find(r => String(r.player_id) === String(p.id));
      row[`p${p.id}`] = found ? found.pillarScore : null;
    });
    return row;
  }), [matchesOrdered, players, allRowsByMatchId]);

  const playerNames = useMemo(() => Object.fromEntries(players.map(p => [`p${p.id}`, p.name])), [players]);
  const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14 };

  return (
    <div style={{ ...card, padding: '18px 18px 8px', marginBottom: 16 }}>
      <div style={{ height: 320 }}>
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={chartData} margin={{ top: 8, right: 16, left: -8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: 'var(--text-muted)', fontSize: 10, fontFamily: 'Inter, sans-serif' }} axisLine={false} tickLine={false} />
            <YAxis domain={[0, 100]} tick={{ fill: 'var(--text-muted)', fontSize: 10, fontFamily: 'Inter, sans-serif' }} axisLine={false} tickLine={false} />
            <Tooltip content={<ComparisonTooltip playerNames={playerNames} />} />
            {players.map(p => (
              <Line key={p.id} dataKey={`p${p.id}`} stroke={colorFor(p.id)} strokeWidth={2.4}
                dot={{ r: 3, fill: colorFor(p.id) }} activeDot={{ r: 5 }} connectNulls={false} isAnimationActive={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function PlayerColorLegend({ players, colorFor }) {
  return (
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
      {players.map(p => (
        <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', background: colorFor(p.id), display: 'inline-block' }} />
          {p.name}
        </div>
      ))}
    </div>
  );
}

// One table per selected player — Technical score + this player's own position's KPI set,
// one column per selected match plus a pooled "Avg" column. Cell formatting reuses
// TechnicalOverview.js's ScoreCell/KpiCell so rate/volume/bonus display logic never drifts
// between the two pages.
function PlayerKpiTableSection({ player, isPrimary, primaryPosition, matchesOrdered, kpiCols, allRowsByMatchId, pooledRowsByPlayerId, colorFor }) {
  const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14 };
  const TH = { padding: '9px 10px', fontSize: '0.67rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)', textAlign: 'center', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap', background: 'var(--bg-app)' };
  const THL = { ...TH, textAlign: 'left' };
  const TDL = { padding: '9px 10px', fontSize: '0.82rem', color: 'var(--text-primary)', fontWeight: 600, whiteSpace: 'nowrap' };

  const rowForMatch = matchId => (allRowsByMatchId[matchId] || []).find(r => String(r.player_id) === String(player.id));
  const pooledRow = pooledRowsByPlayerId[player.id];
  const differentPosition = !isPrimary && player.position !== primaryPosition;

  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, borderLeft: `4px solid ${colorFor(player.id)}`, paddingLeft: 10 }}>
        <div>
          <div style={{ fontSize: '0.98rem', fontWeight: 700, color: 'var(--text-primary)' }}>
            {player.name}
            {isPrimary && <span style={{ fontSize: 11, fontWeight: 700, color: ACCENT, marginLeft: 8 }}>PRIMARY</span>}
          </div>
          {player.position && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{player.position}</div>}
        </div>
      </div>
      {differentPosition && (
        <p style={{ fontSize: 11, color: 'var(--text-dimmed)', margin: '0 0 8px' }}>
          Different position ({player.position}) — showing {primaryPosition}'s KPI set.
        </p>
      )}
      <div style={{ ...card, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 260 + matchesOrdered.length * 90 }}>
          <thead>
            <tr>
              <th style={THL}>KPI</th>
              <th style={{ ...TH, borderLeft: '1px solid var(--border)', borderRight: '1px solid var(--border)' }}>Avg</th>
              {matchesOrdered.map(m => <th key={m.matchId} style={TH}>{m.label}</th>)}
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderTop: '1px solid var(--border)' }}>
              <td style={{ ...TDL, color: ACCENT }}>Technical</td>
              <ScoreCell value={pooledRow?.pillarScore} bold />
              {matchesOrdered.map(m => {
                const r = rowForMatch(m.matchId);
                return <ScoreCell key={m.matchId} value={r?.pillarScore} />;
              })}
            </tr>
            {kpiCols.map(k => {
              const pooledK = (pooledRow?.kpis || []).find(x => x.kpiId === k.kpiId);
              return (
                <tr key={k.kpiId} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={TDL}>{k.kpiName}</td>
                  <KpiCell value={pooledK?.value} count={pooledK?.count} scoringMode={k.scoringMode} />
                  {matchesOrdered.map(m => {
                    const r = rowForMatch(m.matchId);
                    const kv = (r?.kpis || []).find(x => x.kpiId === k.kpiId);
                    return <KpiCell key={m.matchId} value={kv?.value} count={kv?.count} scoringMode={k.scoringMode} />;
                  })}
                </tr>
              );
            })}
            {!kpiCols.length && (
              <tr><td colSpan={2 + matchesOrdered.length} style={{ padding: 16, textAlign: 'center', color: 'var(--text-dimmed)', fontSize: 12.5 }}>No KPI data for this selection.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function TechnicalComparison({ user, onNavigate }) {
  const [players, setPlayers] = useState([]);
  const [matchOptions, setMatchOptions] = useState([]);
  const [primaryPlayerId, setPrimaryPlayerId] = useState('');
  const [compareIds, setCompareIds] = useState([]);
  const [selectedMatchIds, setSelectedMatchIds] = useState([]);
  const [allRowsByMatchId, setAllRowsByMatchId] = useState({});
  const [pooledRowsByPlayerId, setPooledRowsByPlayerId] = useState({});
  const [loadingTrend, setLoadingTrend] = useState(false);

  useEffect(() => {
    Promise.all([getPlayers(), getTechnicalStats()]).then(([pl, stats]) => {
      const active = (pl || [])
        .filter(p => p.active !== false && POSITIONS.includes(p.position))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setPlayers(active);
      if (active.length) setPrimaryPlayerId(String(active[0].id));
      setMatchOptions((stats || []).map(m => ({
        value: String(m.match_id),
        label: `${m.opponent_name || m.session_title || 'Match'} · ${(m.session_date || '').slice(0, 10)}`,
        date: (m.session_date || '').slice(0, 10),
      })));
    }).catch(() => {});
  }, []);

  // Guard against a promoted-to-primary player lingering in the compare list.
  useEffect(() => {
    setCompareIds(ids => ids.filter(id => id !== primaryPlayerId));
  }, [primaryPlayerId]);

  const matchIdsNum = useMemo(() => selectedMatchIds.map(Number), [selectedMatchIds]);

  // technical-kpi-breakdown always returns the whole squad, so this fetch depends only on the
  // match selection — changing the player selection never triggers a network call. One call per
  // selected match for the per-match trend, plus one multi-match call for the pooled "Avg"
  // column — same "per-match calls for the chart, one multi-match call for the pooled table"
  // pattern the /technical-trend endpoint and PlayerTechnical.js already use.
  useEffect(() => {
    if (!matchIdsNum.length) { setAllRowsByMatchId({}); setPooledRowsByPlayerId({}); return; }
    setLoadingTrend(true);
    Promise.all([
      Promise.all(matchIdsNum.map(id => getTechnicalKpiBreakdown('week', [id]).then(rows => [id, rows || []]).catch(() => [id, []]))),
      getTechnicalKpiBreakdown('week', matchIdsNum).catch(() => []),
    ]).then(([perMatch, pooled]) => {
      setAllRowsByMatchId(Object.fromEntries(perMatch));
      setPooledRowsByPlayerId(Object.fromEntries((pooled || []).map(r => [r.player_id, r])));
    }).finally(() => setLoadingTrend(false));
  }, [matchIdsNum]);

  const matchesOrdered = useMemo(() => matchOptions
    .filter(m => selectedMatchIds.includes(m.value))
    .slice()
    .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
    .map(m => ({ matchId: Number(m.value), label: m.label })),
    [matchOptions, selectedMatchIds]);

  const primaryPlayer = players.find(p => String(p.id) === String(primaryPlayerId));
  const comparePlayers = useMemo(() =>
    compareIds.map(id => players.find(p => String(p.id) === String(id))).filter(Boolean),
    [compareIds, players]);
  const selectedPlayers = useMemo(() => primaryPlayer ? [primaryPlayer, ...comparePlayers] : comparePlayers, [primaryPlayer, comparePlayers]);

  // Primary player's position's KPI set drives every table's rows — the Technical score itself
  // is always shown for every selected player regardless of position (it's the one universal,
  // always-comparable number); KPIs that don't apply to a compare player's own position render
  // as "no data" via KpiCell's existing null handling.
  const kpiCols = useMemo(() => {
    const row = pooledRowsByPlayerId[primaryPlayerId];
    return (row?.kpis || []).filter(k => !NAME_BADGE_KPIS.has(k.kpiName));
  }, [pooledRowsByPlayerId, primaryPlayerId]);

  const colorFor = useCallback(playerId => {
    if (String(playerId) === String(primaryPlayerId)) return ACCENT;
    const idx = compareIds.indexOf(String(playerId));
    return idx >= 0 ? COMPARE_COLORS[idx % COMPARE_COLORS.length] : 'var(--text-muted)';
  }, [primaryPlayerId, compareIds]);

  const hasSelection = !!primaryPlayerId && matchIdsNum.length > 0;

  return (
    <PageChrome onNavigate={onNavigate} user={user}>
      <div style={{ width: 'calc(100% - 76px)', margin: '28px 38px 0', paddingBottom: 40 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0, color: ACCENT }}>Technical Comparison</h2>
          <button onClick={() => onNavigate('technical-overview')}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.83rem', fontWeight: 600, fontFamily: 'inherit' }}>
            ← Back to Team Overview
          </button>
        </div>

        <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={primaryPlayerId} onChange={e => setPrimaryPlayerId(e.target.value)}
            style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13 }}>
            {players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <ComparePlayerPicker players={players} selected={compareIds} onChange={setCompareIds} max={MAX_COMPARE} excludeId={primaryPlayerId} />
          <MultiSelect options={matchOptions} selected={selectedMatchIds} onChange={setSelectedMatchIds} placeholder="Select match(es)…" />
        </div>

        {compareIds.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
            {compareIds.map(id => {
              const p = players.find(pl => String(pl.id) === String(id));
              return (
                <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 99, background: 'var(--bg-card)', border: `1px solid ${colorFor(id)}`, fontSize: 12, color: 'var(--text-primary)' }}>
                  {p?.name || id}
                  <X size={12} style={{ cursor: 'pointer' }} onClick={() => setCompareIds(ids => ids.filter(x => x !== id))} />
                </span>
              );
            })}
          </div>
        )}

        {!hasSelection && (
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: 48, textAlign: 'center', color: 'var(--text-dimmed)' }}>
            <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
              Select a player and at least one match to see progress.
            </p>
          </div>
        )}

        {hasSelection && loadingTrend && <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</p>}

        {hasSelection && !loadingTrend && (
          <>
            <PlayerColorLegend players={selectedPlayers} colorFor={colorFor} />
            <TechnicalScoreTrendChart players={selectedPlayers} matchesOrdered={matchesOrdered} allRowsByMatchId={allRowsByMatchId} colorFor={colorFor} />
            {selectedPlayers.map(p => (
              <PlayerKpiTableSection
                key={p.id}
                player={p}
                isPrimary={String(p.id) === String(primaryPlayerId)}
                primaryPosition={primaryPlayer?.position}
                matchesOrdered={matchesOrdered}
                kpiCols={kpiCols}
                allRowsByMatchId={allRowsByMatchId}
                pooledRowsByPlayerId={pooledRowsByPlayerId}
                colorFor={colorFor}
              />
            ))}
          </>
        )}
      </div>
    </PageChrome>
  );
}
